import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { watchlistAPI } from '../services/api';
import { HiOutlineFilm, HiCheck, HiOutlineTrash, HiOutlineX, HiOutlineSearch, HiOutlineShare } from 'react-icons/hi';
import toast from 'react-hot-toast';
import ConfirmModal from '../components/ConfirmModal';
import { RatingBottomSheet } from '../components';

const TMDB_IMAGE_BASE = 'https://image.tmdb.org/t/p/w200';

function MobileMovies() {
  const { t } = useTranslation();
  const [movies, setMovies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [dateSortDirection, setDateSortDirection] = useState('desc');
  const [alphabeticalSortDirection, setAlphabeticalSortDirection] = useState('asc');
  const [searchQuery, setSearchQuery] = useState('');
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    movieId: null,
    event: null,
  });

  useEffect(() => {
    loadMovies();
  }, []);

  const loadMovies = async () => {
    setLoading(true);
    try {
      const response = await watchlistAPI.getMovies();
      setMovies(response.data);
    } catch (error) {
      console.error('Error loading movies:', error);
      toast.error(t('movies.errorLoadingMovies'));
    } finally {
      setLoading(false);
    }
  };

  const toggleWatched = async (movie) => {
    try {
      await watchlistAPI.update(movie.id, { watched: !movie.watched });
      setMovies((prev) =>
        prev.map((m) =>
          m.id === movie.id ? { ...m, watched: !m.watched } : m
        )
      );
      toast.success(movie.watched ? t('watchlist.markedAsUnwatched') : t('watchlist.markedAsWatched'));
    } catch (error) {
      toast.error(t('movies.errorUpdating'));
    }
  };

  const handleRemoveClick = (id, e) => {
    e.stopPropagation();
    setConfirmModal({ isOpen: true, movieId: id, event: e });
  };

  const removeMovie = async () => {
    if (!confirmModal.movieId) return;
    
    try {
      await watchlistAPI.remove(confirmModal.movieId);
      setMovies((prev) => prev.filter((m) => m.id !== confirmModal.movieId));
      toast.success(t('movies.movieRemoved'));
      setConfirmModal({ isOpen: false, movieId: null, event: null });
    } catch (error) {
      toast.error(t('movies.errorRemoving'));
    }
  };

  const handleRate = async (movieId, rating) => {
    try {
      await watchlistAPI.update(movieId, { user_rating: rating });
      setMovies(prev => prev.map(m =>
        m.id === movieId ? { ...m, user_rating: rating } : m
      ));
      toast.success(rating > 0 ? t('rating.saved') : t('rating.removed'));
    } catch (error) {
      toast.error(t('rating.error'));
    }
  };

  const handleDateSort = () => {
    if (sortBy === 'date') {
      setDateSortDirection(dateSortDirection === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy('date');
      setDateSortDirection('desc');
    }
  };

  const handleAlphabeticalSort = () => {
    if (sortBy === 'alphabetical') {
      setAlphabeticalSortDirection(alphabeticalSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy('alphabetical');
      setAlphabeticalSortDirection('asc');
    }
  };

  const handleFilterToggle = () => {
    if (filter === 'all') {
      setFilter('unwatched');
    } else if (filter === 'unwatched') {
      setFilter('watched');
    } else {
      setFilter('all');
    }
  };

  const handleSortTypeToggle = () => {
    if (sortBy === 'date') {
      setSortBy('alphabetical');
    } else {
      setSortBy('date');
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
  };

  const filteredMovies = movies.filter((m) => {
    const matchesFilter =
      filter === 'all' ||
      (filter === 'watched' && m.watched) ||
      (filter === 'unwatched' && !m.watched);

    const matchesSearch =
      searchQuery.trim() === '' ||
      m.title.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesFilter && matchesSearch;
  });

  const sortedMovies = [...filteredMovies].sort((a, b) => {
    if (sortBy === 'date') {
      const dateA = new Date(a.added_at || 0);
      const dateB = new Date(b.added_at || 0);
      return dateSortDirection === 'desc' ? dateB - dateA : dateA - dateB;
    } else if (sortBy === 'alphabetical') {
      const result = a.title.localeCompare(b.title, 'pt-BR');
      return alphabeticalSortDirection === 'asc' ? result : -result;
    }
    return 0;
  });

  const watchedCount = movies.filter((m) => m.watched).length;

  const handleShareList = async () => {
    const rated = movies.filter(m => m.user_rating > 0).sort((a, b) => b.user_rating - a.user_rating);
    const watched = movies.filter(m => m.watched && !m.user_rating);
    const unwatched = movies.filter(m => !m.watched);
    const getYear = (m) => m.release_date ? new Date(m.release_date).getFullYear() : '';

    let text = `🎬 ${t('movies.myMovies')} — My Series\n`;
    text += `📊 ${movies.length} ${t('share.moviesInList')} • ${watchedCount} ${t('share.watchedLabel')}\n`;

    if (rated.length > 0) {
      text += `\n⭐ ${t('share.myRatings')}:\n`;
      rated.forEach(m => {
        const year = getYear(m);
        text += `★ ${m.user_rating} — ${m.title}${year ? ` (${year})` : ''}\n`;
      });
    }
    if (watched.length > 0) {
      text += `\n✅ ${t('share.watched')}:\n`;
      watched.forEach(m => {
        const year = getYear(m);
        text += `• ${m.title}${year ? ` (${year})` : ''}\n`;
      });
    }
    if (unwatched.length > 0) {
      text += `\n🎯 ${t('share.wantToWatch')}:\n`;
      unwatched.forEach(m => {
        const year = getYear(m);
        text += `• ${m.title}${year ? ` (${year})` : ''}\n`;
      });
    }

    const copyToClipboard = (value) => {
      const textarea = document.createElement('textarea');
      textarea.value = value;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      toast.success(t('share.copied'));
    };

    try {
      const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
      if (navigator.share && isMobile) {
        await navigator.share({ title: t('movies.myMovies'), text });
      } else if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        toast.success(t('share.copied'));
      } else {
        copyToClipboard(text);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        try {
          copyToClipboard(text);
        } catch {
          toast.error(t('share.error'));
        }
      }
    }
  };

  // Get filter label
  const getFilterLabel = () => {
    switch (filter) {
      case 'watched':
        return t('watchlist.watched');
      case 'unwatched':
        return t('movies.unwatched');
      default:
        return t('movies.all');
    }
  };

  // Get sort labels
  const getDateSortLabel = () => {
    return dateSortDirection === 'desc' ? 'Recente' : 'Antiga';
  };

  const getAlphaSortLabel = () => {
    return alphabeticalSortDirection === 'asc' ? 'A-Z' : 'Z-A';
  };

  return (
    <div className="mobile-movies" data-scroll-lock>
      {/* Header */}
      <div className="mobile-movies__header">
        <h1>{t('movies.myMovies')}</h1>
        <p>
          {movies.length} {t('movies.moviesInList')} • {watchedCount} {t('movies.watched')}
        </p>
        <button
          className="mobile-movies__pill mobile-movies__pill--share"
          onClick={handleShareList}
          style={{ marginTop: '8px' }}
        >
          <HiOutlineShare size={14} /> {t('share.shareList')}
        </button>
      </div>

      {/* Search Bar */}
      <div className="mobile-movies__search">
        <div className="mobile-movies__search-input-wrapper">
          <HiOutlineSearch className="mobile-movies__search-icon" size={20} />
          <input
            type="text"
            className="mobile-movies__search-input"
            placeholder={t('movies.searchMovie')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery.length > 0 && (
            <button 
              className="mobile-movies__search-clear"
              onClick={clearSearch}
              aria-label="Clear search"
            >
              <HiOutlineX size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Filter & Sort Pills */}
      <div className="mobile-movies__pills-container">
        <div className="mobile-movies__pills">
          <button 
            className="mobile-movies__pill mobile-movies__pill--filter"
            onClick={handleFilterToggle}
          >
            {filter === 'all' && '📋'}
            {filter === 'unwatched' && '👁️'}
            {filter === 'watched' && '✓'} {' '}
            {getFilterLabel()}
          </button>
          <button 
            className={`mobile-movies__pill mobile-movies__pill--date ${sortBy === 'date' ? 'active' : ''}`}
            onClick={() => {
              if (sortBy === 'date') {
                handleDateSort();
              } else {
                setSortBy('date');
              }
            }}
          >
            📅 {getDateSortLabel()}
          </button>
          <button
            className={`mobile-movies__pill mobile-movies__pill--alpha ${sortBy === 'alphabetical' ? 'active' : ''}`}
            onClick={() => {
              if (sortBy === 'alphabetical') {
                handleAlphabeticalSort();
              } else {
                setSortBy('alphabetical');
              }
            }}
          >
            🔤 {getAlphaSortLabel()}
          </button>
        </div>
      </div>

      {/* Movies List */}
      <div className="mobile-movies__list">
        {loading ? (
          <div className="mobile-loading">
            <div className="spinner"></div>
          </div>
        ) : sortedMovies.length > 0 ? (
          sortedMovies.map((movie) => (
            <div key={movie.id} className="mobile-movie-card">
              <div className="mobile-movie-card__poster">
                {movie.poster_path ? (
                  <img
                    src={`${TMDB_IMAGE_BASE}${movie.poster_path}`}
                    alt={movie.title}
                  />
                ) : (
                  <div className="mobile-movie-card__poster-placeholder">
                    <HiOutlineFilm size={32} />
                  </div>
                )}
                {movie.watched && (
                  <div className="mobile-movie-card__watched-badge">
                    <HiCheck size={12} />
                  </div>
                )}
              </div>
              <div className="mobile-movie-card__content">
                <h3 className="mobile-movie-card__title">{movie.title}</h3>
                <p className="mobile-movie-card__meta">
                  {movie.release_date
                    ? new Date(movie.release_date).getFullYear()
                    : t('movies.unknownYear')}
                  {movie.vote_average > 0 && (
                    <span className="mobile-movie-card__rating">
                      {' '}&bull; ⭐ {movie.vote_average.toFixed(1)}
                    </span>
                  )}
                </p>
                <div className="star-rating-with-value">
                  <RatingBottomSheet
                    rating={movie.user_rating || 0}
                    onRate={(rating) => handleRate(movie.id, rating)}
                    title={movie.title}
                  />
                  {movie.user_rating > 0 && (
                    <span className="star-rating-value">{movie.user_rating} / 5</span>
                  )}
                </div>
                <div className="mobile-movie-card__actions">
                  <button
                    className={`mobile-movie-card__btn-watch ${movie.watched ? 'watched' : ''}`}
                    onClick={() => toggleWatched(movie)}
                  >
                    <HiCheck size={16} />
                    {movie.watched ? t('watchlist.watched') : t('watchlist.markAsWatchedShort')}
                  </button>
                  <button
                    className="mobile-movie-card__btn-remove"
                    onClick={(e) => handleRemoveClick(movie.id, e)}
                    title={t('movies.remove')}
                  >
                    <HiOutlineTrash size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="mobile-empty">
            <div className="mobile-empty__icon">
              <HiOutlineFilm size={48} />
            </div>
            <h3>{t('movies.noMoviesFound')}</h3>
            <p>
              {filter !== 'all'
                ? t('movies.noMoviesWithFilter')
                : t('movies.addMoviesFromSearch')}
            </p>
          </div>
        )}
      </div>

      {/* Bottom spacing for tab bar */}
      <div className="mobile-movies__spacer" />

      {/* Confirm Remove Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={t('movies.removeMovie')}
        message={t('movies.confirmRemove')}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        onConfirm={removeMovie}
        onCancel={() => setConfirmModal({ isOpen: false, movieId: null, event: null })}
        type="danger"
      />
    </div>
  );
}

export default MobileMovies;
